import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import {
  ApplicationInsights,
  ICustomProperties,
  SeverityLevel
} from '@microsoft/applicationinsights-web';

// Interface for CSP violation events
interface SecurityPolicyViolationEvent extends Event {
  blockedURI: string;
  violatedDirective: string;
  effectiveDirective: string;
  originalPolicy: string;
  sourceFile: string;
  lineNumber: number;
  columnNumber: number;
  statusCode: number;
  referrer: string;
}

import { ClaimsService } from '@core/authentication/claims.service';
import { AggregatorFeatureFlagService } from '@core/services/aggregator-feature-flag.service';
import { AggregatorFeatureFlag } from '@env/aggregator/aggregator.interface';
import { environment } from '@env/environment';
import { appVersion } from 'src/app/app.version';
import { aggregator } from '@env/aggregator/aggregator';

@Injectable({
  providedIn: 'root'
})
export class MonitoringService {
  appInsights: ApplicationInsights | null = null;

  constructor(
    private claimsService: ClaimsService,
    private aggregatorFeatureFlagService: AggregatorFeatureFlagService,
    @Inject(LOCALE_ID) locale: string
  ) {
    if (
      this.aggregatorFeatureFlagService.isEnabled(
        AggregatorFeatureFlag.AppInsights
      ) &&
      !!environment.appInsightsConnectionString
    ) {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: environment.appInsightsConnectionString,
          enableAutoRouteTracking: false, // Disable auto tracking so we can track with duration manually
          enableCorsCorrelation: true, // the SDK will add two headers (Request-Id and Request-Context) to all CORS requests to correlate outgoing AJAX dependencies with corresponding requests on the server side.
          enableRequestHeaderTracking: true, // AJAX and Fetch request headers are tracked.
          enableResponseHeaderTracking: true, // AJAX and Fetch request response headers are tracked.
          correlationHeaderExcludedDomains: [
            'abr.business.gov.au',
            'google-analytics.com',
            'au-live.inside-graph.com',
            'au-tracker.inside-graph.com'
          ] // exclude third-party domains from having the correlation headers added(to avoid Preflight requests)
        }
      });
      this.appInsights.loadAppInsights();

      this.appInsights.addTelemetryInitializer((envelope) => {
        envelope.tags = envelope.tags || [];
        envelope.tags.push({ 'ai.cloud.role': 'Spark Plus' });
        envelope.tags.push({ 'ai.application.ver': appVersion });
        envelope.tags.push({ 'ai.application.locale': locale });
        
        // Add aggregatorName to all telemetry as a custom dimension
        if (envelope.data) {
          envelope.data.aggregatorName = aggregator.name;
        }
      });

      if (this.claimsService) {
        this.appInsights.setAuthenticatedUserContext(this.claimsService.userId);
      }

      // Set up CSP violation reporting
      this.setupCspViolationReporting();
    }
  }

  /**
   * Sets up Content Security Policy violation reporting to Application Insights
   */
  private setupCspViolationReporting(): void {
    document.addEventListener('securitypolicyviolation', (event: SecurityPolicyViolationEvent) => {
      this.logException(
        new Error('CSP Violation: ' + event.violatedDirective),
        undefined,
        {
          blockedURI: event.blockedURI,
          violatedDirective: event.violatedDirective,
          effectiveDirective: event.effectiveDirective,
          originalPolicy: event.originalPolicy,
          sourceFile: event.sourceFile,
          lineNumber: event.lineNumber,
          columnNumber: event.columnNumber,
          statusCode: event.statusCode,
          referrer: event.referrer
        }
      );
    });
  }

  /**
   * Starts tracking a page view - should be called at the beginning of navigation
   * @param name - Page name (optional, will be used when stopTrackPage is called)
   */
  startTrackPageView(name?: string): void {
    try {
      this.appInsights?.startTrackPage(name);
    } catch (error) {
      // Silently fail - telemetry should never break business logic
      console.warn('Failed to start tracking page view:', error);
    }
  }

  /**
   * Stops tracking and logs a page view with duration
   * @param name - Page name (optional, will use document title if not provided)
   * @param url - Page URL (optional, will use current URL if not provided)
   * @param properties - Custom properties to include
   */
  stopTrackPageView(name?: string, url?: string, properties?: { [key: string]: any }): void {
    try {
      this.appInsights?.stopTrackPage(
        name || document.title,
        url || window.location.href,
        properties
      );
    } catch (error) {
      // Silently fail - telemetry should never break business logic
      console.warn('Failed to stop tracking page view:', error);
    }
  }

  /**
   * Logs a page view (without duration tracking)
   * Uses document.title for the name and optionally accepts a URL
   * @param url - Page URL (optional, will use current URL if not provided)
   */
  logPageView(url?: string): void {
    try {
      this.appInsights?.trackPageView({
        name: document.title,
        uri: url || window.location.href
      });
    } catch (error) {
      // Silently fail - telemetry should never break business logic
      console.warn('Failed to log page view:', error);
    }
  }

  logEvent(name: string, properties?: { [key: string]: any }) {
    try {
      this.appInsights?.trackEvent({ name: name }, properties);
    } catch (error) {
      // Silently fail - telemetry should never break business logic
      console.warn('Failed to log event to Application Insights:', error);
    }
  }

  logMetric(
    name: string,
    average: number,
    properties?: { [key: string]: any }
  ) {
    try {
      this.appInsights?.trackMetric({ name: name, average: average }, properties);
    } catch (error) {
      // Silently fail - telemetry should never break business logic
      console.warn('Failed to log metric to Application Insights:', error);
    }
  }

  logException(
    exception: Error,
    severityLevel?: number,
    customProperties?: ICustomProperties
  ) {
    try {
      this.appInsights?.trackException(
        { exception: exception, severityLevel: severityLevel },
        customProperties
      );
    } catch (error) {
      // Silently fail - telemetry should never break business logic
      console.warn('Failed to log exception to Application Insights:', error);
    }
  }

  logTrace(message: string, properties?: { [key: string]: any }, severityLevel?: SeverityLevel) {
    try {
      this.appInsights?.trackTrace({ message: message, severityLevel: severityLevel }, properties);
    } catch (error) {
      // Silently fail - telemetry should never break business logic
      console.warn('Failed to log trace to Application Insights:', error);
    }
  }
}
